import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { selectUser, getUser } from "../../redux/modules/userSlice";
import { ProfileAPI, UserAPI } from "../api";
import Uploadphotoicon from "../../Assests/Images/uploadphotoicon.png";
import ProfileCropDialog from "../Joinee/ProfileCropDialog";
import { getHrTeamLocation } from "../../config/locationConfig";
import { adminHomePath } from "./adminRoutes";
import {
  ADMIN_BUTTON_SX,
  ADMIN_COLORS,
  ADMIN_FONT,
  ADMIN_ICON_SIZE,
  ADMIN_TYPE,
  AdminCard,
  AdminPage,
  AdminPageHeader,
  AdminSheet,
  AdminSheetHeader,
} from "./adminUi";

var MAX_PHOTO_BYTES = 5 * 1024 * 1024;

/** "9928041117" / "919928041117" → "+91 99280 41117". */
function formatMobile(value) {
  var digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 12 && digits.indexOf("91") === 0) digits = digits.slice(2);
  if (digits.length !== 10) return digits;
  return "+91 " + digits.slice(0, 5) + " " + digits.slice(5);
}

function DetailRow(props) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" },
        gap: { xs: 0.25, sm: 2 },
        py: 1.5,
        borderBottom: "1px solid " + ADMIN_COLORS.borderSoft,
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Typography
        sx={{ fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.textSubtle }}
      >
        {props.label}
      </Typography>
      <Typography
        sx={{
          fontFamily: ADMIN_FONT,
          fontSize: 14,
          fontWeight: 500,
          color: ADMIN_COLORS.text,
          wordBreak: "break-word",
        }}
      >
        {props.value || "—"}
      </Typography>
    </Box>
  );
}

var STAFF_AVATAR_SIZE = { xs: 104, sm: 128 };

/** Bordered block on the staff profile, optionally titled. */
function ProfileSection(props) {
  return (
    <Box
      component="section"
      sx={Object.assign(
        {
          border: "1px solid " + ADMIN_COLORS.lineSoft,
          borderRadius: "12px",
          bgcolor: ADMIN_COLORS.card,
          p: { xs: "16px", sm: "24px" },
          minWidth: 0,
          boxSizing: "border-box",
        },
        props.sx || {}
      )}
    >
      {props.title ? (
        <Typography
          component="h2"
          sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.label, mb: "12px" }, ADMIN_TYPE.sectionTitle)}
        >
          {props.title}
        </Typography>
      ) : null}
      {props.children}
    </Box>
  );
}

function ProfileFieldIcon(props) {
  var Icon = props.icon;
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: "8px",
        bgcolor: ADMIN_COLORS.brandSoft,
        color: ADMIN_COLORS.brand,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: ADMIN_ICON_SIZE.action }} />
    </Box>
  );
}

function ProfileField(props) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        py: "14px",
        borderBottom: "1px solid " + ADMIN_COLORS.lineSoft,
        minWidth: 0,
      }}
    >
      <ProfileFieldIcon icon={props.icon} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.labelSecondary }, ADMIN_TYPE.fieldLabel)}>
          {props.label}
        </Typography>
        <Typography
          sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.label, wordBreak: "break-word" }, ADMIN_TYPE.fieldValue)}
        >
          {props.value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function AdminProfile() {
  var dispatch = useDispatch();
  var navigate = useNavigate();
  var { user } = useSelector(selectUser);
  var isStaff = Boolean(user.isAdmin || user.isSuperAdmin);
  var fileInputRef = useRef(null);

  var [profileData, setProfileData] = useState({});
  var [userdata, setUserdata] = useState({});
  var [loading, setLoading] = useState(true);
  var [resetting, setResetting] = useState(false);
  var [cropOpen, setCropOpen] = useState(false);
  var [cropSrc, setCropSrc] = useState(null);
  var [pendingFileName, setPendingFileName] = useState("");
  var [uploading, setUploading] = useState(false);

  useEffect(
    function () {
      if (!user._id) return;
      setLoading(true);
      ProfileAPI.getUserProfile(user._id)
        .then(function (res) {
          setProfileData((res.data && res.data.obj) || {});
          setUserdata((res.data && res.data.profileDetails) || {});
        })
        .catch(function (err) {
          console.log(err);
        })
        .finally(function () {
          setLoading(false);
        });
    },
    [user._id]
  );

  function handleResetPassword() {
    setResetting(true);
    ProfileAPI.getResetPassword(user._id)
      .then(function (res) {
        if (res.success) {
          toast.success(res.message, { hideProgressBar: true, closeOnClick: true });
        } else {
          toast.error(res.message, { hideProgressBar: true, closeOnClick: true });
        }
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to reset password", { hideProgressBar: true });
      })
      .finally(function () {
        setResetting(false);
      });
  }

  function handleFileChange(event) {
    var file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type || file.type.indexOf("image/") !== 0) {
      toast.error("Please select an image file (JPG or PNG)", { hideProgressBar: true });
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Image must be under 5 MB", { hideProgressBar: true });
      return;
    }
    setPendingFileName(file.name || "profile.jpg");
    var reader = new FileReader();
    reader.onload = function (e) {
      setCropSrc(e.target.result);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  }

  function handleCropClose() {
    if (uploading) return;
    setCropOpen(false);
    setCropSrc(null);
  }

  function handleCropConfirm(dataUrl) {
    if (!dataUrl || !user._id) {
      toast.error("Unable to crop image. Please try again.", { hideProgressBar: true });
      return;
    }
    setUploading(true);
    UserAPI.updateProfilePhoto(user._id, {
      profilePhoto: dataUrl,
      profilePhoto_fileName: pendingFileName,
    })
      .then(function (res) {
        if (!res || res.success === false) {
          toast.error((res && res.message) || "Unable to update profile photo", {
            hideProgressBar: true,
          });
          return;
        }
        var nextPhoto =
          res.data && res.data.profilePhoto ? res.data.profilePhoto : dataUrl;
        setUserdata(function (prev) {
          return Object.assign({}, prev, { profilePicture: nextPhoto });
        });
        dispatch(getUser(Object.assign({}, user, { profilePhoto: nextPhoto })));
        setCropOpen(false);
        setCropSrc(null);
        toast.success("Profile photo updated", { hideProgressBar: true });
      })
      .catch(function () {
        toast.error("Unable to update profile photo", { hideProgressBar: true });
      })
      .finally(function () {
        setUploading(false);
      });
  }

  var photo = userdata.profilePicture || user.profilePhoto || Uploadphotoicon;
  var detailRows = [
    { label: "Full Name", value: userdata.name || user.name },
    { label: "Designation", value: userdata.designation },
  ].concat(
    Object.keys(profileData).map(function (key) {
      var value = profileData[key];
      if (key === "Mobile Number") value = formatMobile(value);
      return { label: key, value: value };
    })
  );
  function goBack() {
    var historyIndex = window.history.state && window.history.state.idx;
    if (historyIndex > 0) {
      navigate(-1);
    } else {
      navigate(adminHomePath(user), { replace: true });
    }
  }

  function openFilePicker() {
    if (fileInputRef.current) fileInputRef.current.click();
  }

  var content = (
    <>
      <AdminPageHeader
        title="My Profile"
        subtitle="Manage your photo and account details."
      />
      {loading ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <CircularProgress size={28} sx={{ color: ADMIN_COLORS.navy }} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
            gap: 2.5,
            alignItems: "stretch",
          }}
        >
          <AdminCard
            sx={{
              textAlign: "center",
              p: { xs: 3, md: 3.5 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ position: "relative", width: 132, height: 132, mx: "auto" }}>
              <Avatar
                src={photo}
                alt={userdata.name || "Profile"}
                sx={{
                  width: 132,
                  height: 132,
                  border: "4px solid " + ADMIN_COLORS.navySoft,
                }}
              />
              <Tooltip title="Change photo">
                <IconButton
                  aria-label="Change profile photo"
                  onClick={openFilePicker}
                  disabled={uploading}
                  sx={{
                    position: "absolute",
                    right: 2,
                    bottom: 2,
                    width: 36,
                    height: 36,
                    bgcolor: ADMIN_COLORS.navy,
                    color: "#fff",
                    border: "3px solid #fff",
                    "&:hover": { bgcolor: ADMIN_COLORS.navyHover },
                  }}
                >
                  <PhotoCameraOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              hidden
              onChange={handleFileChange}
            />
            <Typography
              sx={{
                fontFamily: ADMIN_FONT,
                fontSize: 18,
                fontWeight: 600,
                color: ADMIN_COLORS.text,
                mt: 2,
              }}
            >
              {userdata.name || user.name || "—"}
            </Typography>
            <Typography
              sx={{ fontFamily: ADMIN_FONT, fontSize: 14, color: ADMIN_COLORS.textMuted }}
            >
              {userdata.designation || ""}
            </Typography>
            <Divider sx={{ my: 2.5, width: "100%" }} />
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PhotoCameraOutlinedIcon />}
              onClick={openFilePicker}
              disabled={uploading}
              sx={ADMIN_BUTTON_SX.outlined}
            >
              {uploading ? "Uploading…" : "Update photo"}
            </Button>
            <Typography
              sx={{
                fontFamily: ADMIN_FONT,
                fontSize: 12,
                color: ADMIN_COLORS.textSubtle,
                mt: 1,
              }}
            >
              JPG or PNG, up to 5 MB.
            </Typography>
          </AdminCard>

          <AdminCard title="Account details" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {detailRows.map(function (row) {
              return <DetailRow key={row.label} label={row.label} value={row.value} />;
            })}
            <Box sx={{ flexGrow: 1, minHeight: 24 }} />
          </AdminCard>
        </Box>
      )}

      <ProfileCropDialog
        open={cropOpen}
        imageSrc={cropSrc}
        saving={uploading}
        onClose={handleCropClose}
        onConfirm={handleCropConfirm}
      />
    </>
  );

  if (isStaff) {
    var staffFields = [
      { label: "Full Name", value: userdata.name || user.name, icon: PersonOutlineOutlinedIcon },
      { label: "Designation", value: userdata.designation, icon: WorkOutlineOutlinedIcon },
      { label: "Email", value: profileData.Email || user.email, icon: MailOutlineOutlinedIcon },
      { label: "Mobile Number", value: formatMobile(profileData["Mobile Number"]), icon: PhoneOutlinedIcon },
    ];
    if (user.isAdmin) {
      staffFields.push({ label: "HR Team Location", value: getHrTeamLocation(user), icon: PlaceOutlinedIcon });
    }
    return (
      <AdminPage>
        <AdminSheet>
          <AdminSheetHeader title="My Profile" onBack={goBack} backLabel="Go back" />
          {loading ? (
            <Box sx={{ py: 10, textAlign: "center" }}>
              <CircularProgress size={30} sx={{ color: ADMIN_COLORS.brand }} />
            </Box>
          ) : (
            <Box
              sx={{
                p: { xs: "12px", sm: "20px" },
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "320px 1fr", lg: "360px 1fr" },
                gap: { xs: "12px", sm: "20px" },
                alignItems: "stretch",
              }}
            >
              <ProfileSection
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  height: "100%",
                }}
              >
                <Box sx={{ position: "relative", width: STAFF_AVATAR_SIZE, height: STAFF_AVATAR_SIZE }}>
                  <Avatar
                    src={photo}
                    alt={userdata.name || "Profile"}
                    sx={{
                      width: STAFF_AVATAR_SIZE,
                      height: STAFF_AVATAR_SIZE,
                      border: "4px solid " + ADMIN_COLORS.brandSoft,
                    }}
                  />
                  <Tooltip title="Change photo">
                    <IconButton
                      aria-label="Change profile photo"
                      onClick={openFilePicker}
                      disabled={uploading}
                      sx={{
                        position: "absolute",
                        right: 4,
                        bottom: 4,
                        width: 40,
                        height: 40,
                        bgcolor: ADMIN_COLORS.brand,
                        color: "#FFFFFF",
                        border: "3px solid " + ADMIN_COLORS.card,
                        "&:hover": { bgcolor: ADMIN_COLORS.brandHover },
                        "&.Mui-disabled": { bgcolor: "#9AA2F0", color: "#FFFFFF" },
                      }}
                    >
                      <PhotoCameraOutlinedIcon sx={{ fontSize: ADMIN_ICON_SIZE.action }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  hidden
                  onChange={handleFileChange}
                />
                <Typography
                  sx={Object.assign(
                    { fontFamily: ADMIN_FONT, color: ADMIN_COLORS.label, mt: "16px", wordBreak: "break-word" },
                    ADMIN_TYPE.profileName
                  )}
                >
                  {userdata.name || user.name || "—"}
                </Typography>
                {userdata.designation ? (
                  <Typography
                    sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.labelSecondary, mt: "2px" }, ADMIN_TYPE.body)}
                  >
                    {userdata.designation}
                  </Typography>
                ) : null}
                <Divider sx={{ my: "20px", width: "100%", borderColor: ADMIN_COLORS.lineSoft }} />
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    uploading ? (
                      <CircularProgress size={ADMIN_ICON_SIZE.button} color="inherit" />
                    ) : (
                      <PhotoCameraOutlinedIcon sx={{ fontSize: ADMIN_ICON_SIZE.button }} />
                    )
                  }
                  onClick={openFilePicker}
                  disabled={uploading}
                  sx={[ADMIN_BUTTON_SX.outlined, ADMIN_BUTTON_SX.withStartIcon]}
                >
                  {uploading ? "Uploading…" : "Update photo"}
                </Button>
                <Typography
                  sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.labelMuted, mt: "8px" }, ADMIN_TYPE.fieldLabel, {
                    fontWeight: 400,
                  })}
                >
                  JPG or PNG, up to 5 MB
                </Typography>
              </ProfileSection>

              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "12px", sm: "20px" }, minWidth: 0 }}>
                <ProfileSection title="Account Details" sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr", lg: "1fr 1fr" },
                      columnGap: "24px",
                    }}
                  >
                    {staffFields.map(function (field) {
                      return <ProfileField key={field.label} label={field.label} value={field.value} icon={field.icon} />;
                    })}
                  </Box>
                </ProfileSection>

                <ProfileSection title="Security">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      flexWrap: "wrap",
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: "16px",
                      p: "16px",
                      borderRadius: "8px",
                      bgcolor: ADMIN_COLORS.page,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flex: "1 1 260px", minWidth: 0 }}>
                      <ProfileFieldIcon icon={LockResetOutlinedIcon} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.label }, ADMIN_TYPE.fieldValue)}>
                          Password
                        </Typography>
                        <Typography sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.labelSecondary }, ADMIN_TYPE.body)}>
                          A new password will be emailed to your registered email address.
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={
                        resetting ? (
                          <CircularProgress size={ADMIN_ICON_SIZE.button} color="inherit" />
                        ) : (
                          <LockResetOutlinedIcon sx={{ fontSize: ADMIN_ICON_SIZE.button }} />
                        )
                      }
                      onClick={handleResetPassword}
                      disabled={resetting}
                      sx={[ADMIN_BUTTON_SX.contained, ADMIN_BUTTON_SX.withStartIcon, { flexShrink: 0 }]}
                    >
                      Reset password
                    </Button>
                  </Box>
                </ProfileSection>
              </Box>
            </Box>
          )}
        </AdminSheet>

        <ProfileCropDialog
          open={cropOpen}
          imageSrc={cropSrc}
          saving={uploading}
          onClose={handleCropClose}
          onConfirm={handleCropConfirm}
        />
      </AdminPage>
    );
  }
  return <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto", p: { xs: 2, md: 3 } }}>{content}</Box>;
}

export default AdminProfile;
